import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { FirestoreSemanticData } from "reedy-data/models";
import { Text } from "components/text";
import { Badge } from "components/ui/badge";

type Character = NonNullable<FirestoreSemanticData["characters"]>[number];

interface CharactersSectionProps {
  characters: Character[];
  contentLanguage: string | undefined;
}

interface CharacterCardProps {
  character: Character;
  contentLanguage: string | undefined;
}

interface CharacterBadge {
  key: "role" | "pronouns" | "age";
  value: string | number;
  variant: "default" | "secondary";
}

const CharacterCard = ({ character, contentLanguage }: CharacterCardProps) => {
  const { t } = useTranslation(["productDetails"]);
  const titleId = useId();
  const name = character.name?.trim() || undefined;
  const role = character.role?.trim() || undefined;
  const description = character.description?.trim() || undefined;
  const age =
    typeof character.age === "string"
      ? character.age.trim() || undefined
      : character.age;
  const pronouns = character.pronouns?.trim() || undefined;
  const aliases = character.aliases
    ?.map((alias) => alias.trim())
    .filter(Boolean)
    .join(", ");
  const badges: CharacterBadge[] = [];
  if (role) badges.push({ key: "role", value: role, variant: "default" });
  if (pronouns) {
    badges.push({ key: "pronouns", value: pronouns, variant: "secondary" });
  }
  if (age !== null && age !== undefined) {
    badges.push({ key: "age", value: age, variant: "secondary" });
  }

  return (
    <article
      aria-labelledby={name ? titleId : undefined}
      aria-label={
        !name
          ? (role ?? pronouns ?? (age != null ? String(age) : undefined))
          : undefined
      }
      className="space-y-2 px-5 py-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        {name && (
          <Text id={titleId} as="h3" variant="title" lang={contentLanguage}>
            {name}
          </Text>
        )}
        {badges.map(({ key, value, variant }) => (
          <Badge key={key} variant={variant} lang={contentLanguage}>
            {value}
          </Badge>
        ))}
      </div>
      {aliases && (
        <Text variant="mutedItalic">
          {t("productDetails:summary.characters.aka")}{" "}
          <Text as="span" variant="mutedItalic" lang={contentLanguage}>
            {`"${aliases}"`}
          </Text>
        </Text>
      )}
      {description && (
        <Text variant="body" className="text-pretty" lang={contentLanguage}>
          {description}
        </Text>
      )}
    </article>
  );
};

export const CharactersSection = ({
  characters,
  contentLanguage,
}: CharactersSectionProps) => {
  const { t } = useTranslation(["productDetails"]);
  const occurrences = new Map<string, number>();
  const keyedCharacters = characters.map((character) => {
    const signature = [
      character.name,
      character.age,
      character.pronouns,
      character.role,
      character.description,
      ...(character.aliases ?? []),
    ].join("|");
    const occurrence = occurrences.get(signature) ?? 0;
    occurrences.set(signature, occurrence + 1);
    return { character, key: `${signature}-${occurrence}` };
  });

  return (
    <section aria-labelledby="characters-title" className="flex flex-col gap-5">
      <div className="space-y-1">
        <Text
          id="characters-title"
          as="h2"
          variant="heading"
          className="text-balance"
        >
          {t("productDetails:summary.sections.characters")}
        </Text>
        <Text variant="muted" className="text-pretty">
          {t("productDetails:summary.characters.subline")}
        </Text>
      </div>

      <div className="divide-y divide-secondary-200 overflow-hidden rounded-xl border border-secondary-200 bg-white">
        {keyedCharacters.map(({ character, key }) => (
          <CharacterCard
            key={key}
            character={character}
            contentLanguage={contentLanguage}
          />
        ))}
      </div>
    </section>
  );
};
